import React from 'react';
import { Typography, COLORS, Tooltip, IconButton, Tag, TableRow, TableCell } from 'pvs-design-system';
import { Box, Stack } from '@mui/material';
import Info from '@mui/icons-material/Info';
import Edit from '@mui/icons-material/Edit';
import Delete from '@mui/icons-material/Delete';
import type { Resource } from 'types';
import { getItemTypeLabel } from 'utils/fileUtils';
import { LIBRARY_ROW_SX } from 'constants/styles';

interface ResourceTableRowProps {
  resource: Resource;
  onOpen: (resource: Resource) => void;
  onShowDetails: (resource: Resource, e?: React.MouseEvent) => void;
  onEdit: (resource: Resource) => void;
  onDelete: (resource: Resource) => void;
  canEdit: boolean;
  canDelete: boolean;
  /** Highlight the row as selected (used by the browse page's details panel). */
  selected?: boolean;
  /**
   * Reserve a left border so selecting a row does not shift its content.
   * The browse page enables this; the home page (no selection styling) does not.
   */
  showSelectionBorder?: boolean;
}

/** A resource row in the list/table view of the browse and home pages. */
const ResourceTableRow: React.FC<ResourceTableRowProps> = ({
  resource,
  onOpen,
  onShowDetails,
  onEdit,
  onDelete,
  canEdit,
  canDelete,
  selected = false,
  showSelectionBorder = false,
}) => (
  <TableRow
    tabIndex={0}
    role="button"
    onClick={() => onOpen(resource)}
    onKeyDown={(e: React.KeyboardEvent) => {
      // Only act when the row itself is focused, so Enter/Space on a nested
      // Info/Edit/Delete button doesn't bubble up and also open the resource.
      if (e.target === e.currentTarget && (e.key === 'Enter' || e.key === ' ')) {
        e.preventDefault();
        onOpen(resource);
      }
    }}
    selected={selected}
    sx={{
      ...LIBRARY_ROW_SX,
      ...(showSelectionBorder
        ? { borderLeft: selected ? `3px solid ${COLORS.purple[700]}` : '3px solid transparent' }
        : {}),
    }}
  >
    <TableCell align="center">
      <Tag variant="default" label={getItemTypeLabel(resource.type)} size="small" />
    </TableCell>
    <TableCell>
      <Typography
        variant="interactive-sm"
        sx={{
          color: COLORS.gray[900],
          lineHeight: 1.3,
          display: 'block',
          cursor: 'pointer',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          '&:hover': { color: COLORS.purple[700], textDecoration: 'underline' },
        }}
      >
        {resource.title}
      </Typography>
      {resource.description && (
        <Typography
          variant="p-sm"
          style={{ color: COLORS.gray[400], fontSize: 11, display: 'block', lineHeight: 1.4, marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
        >
          {resource.description}
        </Typography>
      )}
    </TableCell>
    <TableCell />
    <TableCell>
      <Stack direction="row" gap={0.5} onClick={(e) => e.stopPropagation()}>
        <Tooltip title="View details" placement="top" arrow>
          <Box component="span">
            <IconButton
              size="small"
              color="primary"
              aria-label={`View details for ${resource.title}`}
              onClick={(e: React.MouseEvent) => onShowDetails(resource, e)}
            >
              <Info fontSize="small" />
            </IconButton>
          </Box>
        </Tooltip>
        {canEdit && (
          <Tooltip title="Edit resource" placement="top" arrow>
            <Box component="span">
              <IconButton size="small" color="primary" aria-label="Edit resource" onClick={() => onEdit(resource)}>
                <Edit fontSize="small" />
              </IconButton>
            </Box>
          </Tooltip>
        )}
        {canDelete && (
          <Tooltip title="Delete resource" placement="top" arrow>
            <Box component="span">
              <IconButton size="small" color="primary" aria-label="Delete resource" onClick={() => onDelete(resource)}>
                <Delete fontSize="small" />
              </IconButton>
            </Box>
          </Tooltip>
        )}
      </Stack>
    </TableCell>
  </TableRow>
);

export default React.memo(ResourceTableRow);
